/**
 * Shared token-only styling for the host guest tracker. The table is a plain
 * semantic <table> rather than a data grid: 137 rows of host-entered truth need
 * real table semantics for screen readers, and a grid dependency would be a new
 * bundle for a screen that renders text inputs.
 *
 * Every value here resolves through a `--tsb-*` variable defined in
 * src/styles/guest-table-tokens.css. Radii come from the sanctioned set only
 * (4/8/12/16/24/999) and no surface carries a border and a shadow at the same
 * time.
 */

export const TRACKER_SURFACE_SX = {
  bgcolor: 'var(--tsb-bg-surface)',
  border: '1px solid var(--tsb-border-soft)',
  borderRadius: 'var(--tsb-radius-md)',
  boxShadow: 'none',
} as const;

/**
 * The open record. A green outline is the whole signal — border OR shadow,
 * never both, so the card stays flat and only its edge changes.
 */
export const TRACKER_ACTIVE_SURFACE_SX = {
  ...TRACKER_SURFACE_SX,
  borderColor: 'var(--tsb-brand-border)',
} as const;

/**
 * The small uppercase label that opens a stat group and every record section.
 * Colour is set per use: muted above numbers, brand ink above fields.
 */
export const TRACKER_EYEBROW_SX = {
  fontFamily: 'var(--tsb-font-sans)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.1em',
  lineHeight: 1.4,
  textTransform: 'uppercase',
  flexShrink: 0,
} as const;

/** One labelled stat group — a small bordered card, never a shadowed tile. */
export const TRACKER_STAT_CARD_SX = {
  ...TRACKER_SURFACE_SX,
  borderRadius: 'var(--tsb-radius-sm)',
  px: 1.75,
  py: 1.25,
  minWidth: 0,
} as const;

/** The hairline that separates a stat label from its numbers. */
export const TRACKER_HAIRLINE_SX = {
  width: '1px',
  alignSelf: 'stretch',
  minHeight: 16,
  bgcolor: 'var(--tsb-gray-100)',
  flexShrink: 0,
} as const;

/** A record section: an inset well inside the record card. */
export const TRACKER_SECTION_TILE_SX = {
  bgcolor: 'var(--tsb-bg-elevated)',
  border: '1px solid var(--tsb-border-soft)',
  borderRadius: 'var(--tsb-radius-md)',
  boxShadow: 'none',
  p: 2,
  minWidth: 0,
} as const;

/** "+ Track something new" — the one dashed affordance on the screen. */
export const TRACKER_DASHED_TILE_SX = {
  border: '1px dashed var(--tsb-border)',
  borderRadius: 'var(--tsb-radius-md)',
  bgcolor: 'transparent',
  boxShadow: 'none',
  p: 2,
  minWidth: 0,
  transition: 'border-color var(--tsb-dur-base) var(--tsb-ease-out)',
} as const;

export const TRACKER_TABLE_SX = {
  width: '100%',
  minWidth: 940,
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontFamily: 'var(--tsb-font-sans)',
  '& th, & td': {
    textAlign: 'left',
    verticalAlign: 'middle',
    px: 1.5,
    py: 1,
    color: 'var(--tsb-fg)',
    fontSize: 13,
    // Row rules sit a step lighter than the card outline so the table reads as
    // one surface rather than a stack of boxes.
    borderBottom: '1px solid var(--tsb-gray-100)',
  },
  '& thead th': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    bgcolor: 'var(--tsb-bg-elevated)',
    color: 'var(--tsb-fg-muted)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--tsb-border-soft)',
  },
  // The first cell of every row is a <th scope="row">, so row states have to
  // name both element types or the Guest column stays untinted.
  '& tbody tr:hover th, & tbody tr:hover td': { bgcolor: 'var(--tsb-bg-elevated)' },
  '& tbody tr:last-of-type th, & tbody tr:last-of-type td': { borderBottom: 'none' },
} as const;

/**
 * Doubled `&&` on purpose: the table's own hover rule carries more specificity
 * than a plain row selector, so without it the selected row loses its tint the
 * moment the pointer crosses it.
 */
export const TRACKER_SELECTED_ROW_SX = {
  '&& th, && td': { bgcolor: 'var(--tsb-brand-soft)' },
  '&&:hover th, &&:hover td': { bgcolor: 'var(--tsb-brand-tint)' },
} as const;

/** Inline editors sit flush in the cell — a boxed input per cell reads as a form. */
export const TRACKER_CELL_INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    minHeight: 34,
    borderRadius: 'var(--tsb-radius-sm)',
    bgcolor: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tsb-border)' },
    '&.Mui-focused': { bgcolor: 'var(--tsb-bg-surface)', boxShadow: 'var(--tsb-focus-ring)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tsb-brand)' },
  },
  '& .MuiOutlinedInput-input': {
    px: 1,
    py: 0.75,
    color: 'var(--tsb-fg)',
    fontSize: 13,
    fontWeight: 500,
  },
} as const;

export const TRACKER_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--tsb-radius-sm)',
    bgcolor: 'var(--tsb-bg-surface)',
    fontSize: 13,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tsb-border)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tsb-brand-hover)' },
    '&.Mui-focused': { boxShadow: 'var(--tsb-focus-ring)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tsb-brand)' },
  },
  '& .MuiInputBase-input': { color: 'var(--tsb-fg)', fontWeight: 500 },
  '& .MuiInputLabel-root': { color: 'var(--tsb-fg-muted)', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--tsb-brand-ink)' },
  '& .MuiFormHelperText-root': { color: 'var(--tsb-fg-muted)', fontSize: 11.5, mx: 0, mt: 0.75 },
  // Last on purpose: the error state has to outrank the brand hover/focus
  // rules above, or a rejected field would look like a focused one.
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--tsb-danger)',
  },
  '& .MuiOutlinedInput-root.Mui-error.Mui-focused': {
    boxShadow: '0 0 0 3px var(--tsb-danger-bg)',
  },
  '& .MuiInputLabel-root.Mui-error, & .MuiFormHelperText-root.Mui-error': {
    color: 'var(--tsb-danger-ink)',
  },
} as const;

/**
 * RSVP pill tones. "Can't go" is a real red rather than another grey chip —
 * on a 100-row roster the one state a host has to act on cannot look like the
 * state they are still waiting on.
 */
export const TRACKER_GOING_TONE: Readonly<Record<string, { bg: string; fg: string }>> = {
  going: { bg: 'var(--tsb-brand-tint)', fg: 'var(--tsb-brand-ink)' },
  maybe: { bg: 'var(--tsb-warning-bg)', fg: 'var(--tsb-warning-ink)' },
  cant_go: { bg: 'var(--tsb-danger-bg)', fg: 'var(--tsb-danger-ink)' },
  invited: { bg: 'var(--tsb-bg-elevated)', fg: 'var(--tsb-fg-muted)' },
};

/** The RSVP pill as read-only text — the record header, never a control. */
export const TRACKER_PILL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 'var(--tsb-radius-pill)',
  px: 1.25,
  py: 0.375,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
} as const;

/*
  The three button tones the guest components share. They came from the guest
  app's TogetherGuestPrimitives, which cannot be ported (it pulls in next/image,
  next/link and an SVG asset), so they live here instead.

  `--tsb-on-brand` and `--tsb-brand-active` are not in the token sheet, so both
  carry an inline fallback: white text clears AA on either green, and the active
  step is one shade below `--tsb-brand-hover`. Defining the tokens later wins
  automatically.
*/

export const GUEST_PRIMARY_BUTTON_SX = {
  borderRadius: 'var(--tsb-radius-sm)',
  bgcolor: 'var(--tsb-brand-hover)',
  color: 'var(--tsb-on-brand, #ffffff)',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: 'var(--tsb-brand-active, #233729)',
    boxShadow: 'none',
  },
  '&.Mui-focusVisible': {
    boxShadow: 'var(--tsb-focus-ring)',
  },
} as const;

export const GUEST_SECONDARY_BUTTON_SX = {
  borderRadius: 'var(--tsb-radius-sm)',
  borderColor: 'var(--tsb-brand-border)',
  color: 'var(--tsb-brand-ink)',
  bgcolor: 'var(--tsb-bg-surface)',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    borderColor: 'var(--tsb-brand-hover)',
    bgcolor: 'var(--tsb-brand-tint)',
  },
  '&.Mui-focusVisible': {
    boxShadow: 'var(--tsb-focus-ring)',
  },
} as const;

export const GUEST_TEXT_BUTTON_SX = {
  borderRadius: 'var(--tsb-radius-sm)',
  color: 'var(--tsb-brand-ink)',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { bgcolor: 'var(--tsb-brand-tint)' },
  '&.Mui-focusVisible': { boxShadow: 'var(--tsb-focus-ring)' },
} as const;
